// @ts-nocheck
/**
 * chat-history.js — History drawer, conversation resume, restore, and "Forget Me".
 *
 * Manages the slide-out drawer that shows past conversations,
 * loading/resuming archived chats, restoring the active conversation
 * on page refresh, and PDPA/GDPR data erasure.
 *
 * Dependencies: chat-fetch.js, chat-identity.js, chat-render.js
 *
 * Exposes:
 *   window.AIFlorist.history.init(deps)
 *   window.AIFlorist.history.restoreActive(cid) → Promise<boolean>
 *   window.AIFlorist.history.toggle()
 *   window.AIFlorist.history.open()
 *   window.AIFlorist.history.close()
 *   window.AIFlorist.history.isOpen
 */
(function () {
  'use strict';

  window.AIFlorist = window.AIFlorist || {};
  var debug = window.AIFlorist.debug || { log: function () {}, warn: function () {}, error: function () {} };

  var fetch = null;        // AIFlorist.fetch
  var render = null;       // AIFlorist.render
  var identity = null;     // AIFlorist.identity

  // ── State ──────────────────────────────────────────────────────────
  var isOpen = false;
  var workerUrl = '';
  var drawerEl = null;
  var drawerOverlayEl = null;
  var drawerListEl = null;
  var historyBtnEl = null;

  // Callbacks provided by the orchestrator
  var onConversationLoaded = null; // fn(conversationId, title)
  var onForgetComplete = null;     // fn()
  var uiLanguage = 'en';
  var DICT = {
    en: {
      enableMemory: 'Enable memory to see past chats',
      loading: 'Loading...',
      noPastConversations: 'No past conversations yet',
      unableLoadHistory: 'Unable to load history',
      unableLoadConversation: 'Unable to load that conversation. Please try again.',
      forgetConfirm: 'This will permanently delete all your chat history and preferences. This cannot be undone. Continue?',
      deletedFresh: "All your data has been deleted. You're starting fresh.",
      unableDelete: 'Unable to delete your data right now. Please try again.',
      msgs: 'msgs',
    },
    zh: {
      enableMemory: '启用记忆后可查看历史对话',
      loading: '加载中...',
      noPastConversations: '暂无历史对话',
      unableLoadHistory: '无法加载历史记录',
      unableLoadConversation: '无法加载该对话，请重试。',
      forgetConfirm: '这将永久删除你的所有聊天记录和偏好，且无法撤销。是否继续？',
      deletedFresh: '你的数据已全部删除。你现在将从新会话开始。',
      unableDelete: '暂时无法删除你的数据，请稍后重试。',
      msgs: '条消息',
    },
    ja: {
      enableMemory: 'メモリを有効にすると過去のチャットを表示できます',
      loading: '読み込み中...',
      noPastConversations: '過去のチャットはまだありません',
      unableLoadHistory: '履歴を読み込めませんでした',
      unableLoadConversation: 'この会話を読み込めませんでした。もう一度お試しください。',
      forgetConfirm: 'チャット履歴と設定がすべて完全に削除されます。この操作は取り消せません。続行しますか？',
      deletedFresh: 'データはすべて削除されました。新しいチャットとして開始します。',
      unableDelete: '現在データを削除できません。しばらくしてからお試しください。',
      msgs: '件',
    },
  };

  // ── Init ───────────────────────────────────────────────────────────

  /**
   * @param {object} deps
   * @param {string} deps.workerUrl
   * @param {HTMLElement} deps.drawerEl
   * @param {HTMLElement} deps.drawerOverlayEl
   * @param {HTMLElement} deps.drawerListEl
   * @param {HTMLElement} deps.historyBtnEl
   * @param {function} deps.onConversationLoaded - (conversationId, title) => void
   * @param {function} deps.onForgetComplete - () => void
   */
  function init(deps) {
    fetch = window.AIFlorist.fetch;
    render = window.AIFlorist.render;
    identity = window.AIFlorist.identity;

    workerUrl = deps.workerUrl;
    drawerEl = deps.drawerEl;
    drawerOverlayEl = deps.drawerOverlayEl;
    drawerListEl = deps.drawerListEl;
    historyBtnEl = deps.historyBtnEl;
    onConversationLoaded = deps.onConversationLoaded || null;
    onForgetComplete = deps.onForgetComplete || null;
    uiLanguage = detectLanguage();
  }

  // ── Drawer open/close ──────────────────────────────────────────────

  function toggle() {
    if (isOpen) { close(); } else { open(); }
  }

  function open() {
    isOpen = true;
    drawerEl.classList.add('afc-drawer--open');
    drawerOverlayEl.classList.add('afc-drawer-overlay--open');
    if (historyBtnEl) historyBtnEl.classList.add('afc-header-btn--active');
    loadChatHistory();
  }

  function close() {
    isOpen = false;
    drawerEl.classList.remove('afc-drawer--open');
    drawerOverlayEl.classList.remove('afc-drawer-overlay--open');
    if (historyBtnEl) historyBtnEl.classList.remove('afc-header-btn--active');
  }

  // ── Restore active conversation (page refresh) ────────────────────

  /**
   * Fetch the current conversation's message history from the Worker DO
   * and re-render all messages.
   *
   * Returns true if messages were restored, false if the caller should
   * show the welcome message instead.
   *
   * In layman's terms: the customer walks back in and Fleur flips
   * through her notepad to show what they already discussed.
   *
   * @param {string} cid - The conversation ID from localStorage
   * @returns {Promise<boolean>}
   */
  async function restoreActive(cid) {
    try {
      var res = await fetch(
        workerUrl + '/conversation/' + cid + '/messages',
        { method: 'GET', headers: buildRequesterHeaders() }
      );
      if (!res.ok) return false;

      var data = await res.json();
      if (data && data.sessionValid === false) return false;
      // Seed the transport layer's state cache so revision turns sent right
      // after page refresh use the longer generation timeout.
      var transport = window.AIFlorist && window.AIFlorist.send;
      if (data && data.state && transport && transport.setConversationState) {
        transport.setConversationState(data.state);
      }
      if (data && Array.isArray(data.history) && data.history.length > 0) {
        render.clearMessages();
        for (var i = 0; i < data.history.length; i++) {
          var msg = data.history[i];
          // Latch the post-generation flag if any prior assistant message
          // shipped an image — covers the orchestrator's "ask, then bake"
          // flow where live state can sit at gathering_criteria after a
          // generation. Without this, post-refresh revision turns would use
          // the short timeout and risk premature abort.
          if (msg && msg.role === 'assistant' && transport && transport.noteAssistantMetadata) {
            transport.noteAssistantMetadata(msg.metadata);
          }
          if (msg.role === 'user' || msg.role === 'assistant') {
            var userImageDataUrl = null;
            if (msg.role === 'user' && msg.image && msg.image.mimeType && msg.image.data) {
              userImageDataUrl = 'data:' + msg.image.mimeType + ';base64,' + msg.image.data;
            }
            render.message(
              msg.role,
              msg.content,
              msg.metadata || null,
              userImageDataUrl,
              null,
              null,
              {
                replyTo: msg.replyTo || null,
                timestamp: msg.timestamp,
              }
            );
          }
        }
        return true;
      }
    } catch (err) {
      debug.error('[AI Florist] Conversation restore failed:', err);
    }
    return false;
  }

  // ── Load history list ──────────────────────────────────────────────

  async function loadChatHistory() {
    var idState = identity.state;

    if (!idState.consentGiven && !idState.customerLoggedIn) {
      drawerListEl.innerHTML =
        '<div class="afc-drawer-empty">' + escapeHtml(t('enableMemory')) + '</div>';
      return;
    }

    drawerListEl.innerHTML =
      '<div class="afc-drawer-empty">' + escapeHtml(t('loading')) + '</div>';

    try {
      var customerIdentity = identity.rebuild();
      var res = await fetch(
        workerUrl + '/conversations?customerId=' + encodeURIComponent(customerIdentity.id),
        { method: 'GET', headers: buildRequesterHeaders(customerIdentity.id) }
      );

      if (!res.ok) throw new Error('Failed to load history');

      var data = await res.json();
      var conversations = data.conversations || [];

      if (conversations.length === 0) {
        drawerListEl.innerHTML =
          '<div class="afc-drawer-empty">' + escapeHtml(t('noPastConversations')) + '</div>';
        return;
      }

      drawerListEl.innerHTML = conversations.map(function (conv) {
        var badgeClass = render.getBadgeClass(conv.state);
        var badgeLabel = render.getBadgeLabel(conv.state);
        var dateStr = render.formatDate(conv.createdAt);

        return (
          '<button class="afc-drawer-item" data-id="' + render.escapeHtml(conv.id) + '">' +
            '<div class="afc-drawer-item-title">' + render.escapeHtml(conv.title) + '</div>' +
            '<div class="afc-drawer-item-meta">' +
              '<span>' + dateStr + '</span>' +
              '<span class="afc-drawer-item-badge ' + badgeClass + '">' + badgeLabel + '</span>' +
              (conv.messageCount ? '<span>' + conv.messageCount + ' ' + escapeHtml(t('msgs')) + '</span>' : '') +
            '</div>' +
            (conv.lastMessagePreview
              ? '<div class="afc-drawer-item-preview">' + render.escapeHtml(conv.lastMessagePreview) + '</div>'
              : '') +
          '</button>'
        );
      }).join('');

      // Bind click handlers
      var items = drawerListEl.querySelectorAll('.afc-drawer-item');
      for (var i = 0; i < items.length; i++) {
        items[i].addEventListener('click', handleHistoryItemClick);
      }
    } catch (err) {
      debug.error('[AI Florist] History load error:', err);
      drawerListEl.innerHTML =
        '<div class="afc-drawer-empty">' + escapeHtml(t('unableLoadHistory')) + '</div>';
    }
  }

  // ── Click on a past conversation ───────────────────────────────────

  async function handleHistoryItemClick(e) {
    var btn = e.currentTarget;
    var archiveId = btn.dataset.id;
    if (!archiveId) return;

    close();

    try {
      var res = await fetch(
        workerUrl + '/conversations/' + archiveId + '/history',
        { method: 'GET', headers: buildRequesterHeaders() }
      );

      if (!res.ok) throw new Error('Failed to load conversation');

      var data = await res.json();
      var title = data.title || null;
      var history = data.history || [];

      // Render the archived messages
      render.clearMessages();
      for (var i = 0; i < history.length; i++) {
        var msg = history[i];
        if (msg.role === 'user' || msg.role === 'assistant') {
          var userImageDataUrl = null;
          if (msg.role === 'user' && msg.image && msg.image.mimeType && msg.image.data) {
            userImageDataUrl = 'data:' + msg.image.mimeType + ';base64,' + msg.image.data;
          }
          render.message(
            msg.role,
            msg.content,
            msg.metadata,
            userImageDataUrl,
            null,
            null,
            {
              replyTo: msg.replyTo || null,
              timestamp: msg.timestamp,
            }
          );
        }
      }

      // Resume: create a new DO hydrated with the archived history
      var resumeRes = await fetch(
        workerUrl + '/conversations/' + archiveId + '/resume',
        {
          method: 'POST',
          headers: buildRequesterHeaders({ 'Content-Type': 'application/json' }),
        }
      );

      var newConversationId = null;
      if (resumeRes.ok) {
        var resumeData = await resumeRes.json();
        newConversationId = resumeData.conversationId;
      }

      if (onConversationLoaded) {
        onConversationLoaded(newConversationId, title);
      }
    } catch (err) {
      debug.error('[AI Florist] Resume error:', err);
      render.systemError(t('unableLoadConversation'));
    }
  }

  // ── Forget Me (PDPA/GDPR erasure) ─────────────────────────────────

  async function handleForgetMe() {
    if (!confirm(t('forgetConfirm'))) {
      return;
    }

    try {
      var customerIdentity = identity.rebuild();
      await fetch(
        workerUrl + '/customer/' + encodeURIComponent(customerIdentity.id),
        { method: 'DELETE', headers: buildRequesterHeaders(customerIdentity.id) }
      );

      // Clear local storage
      identity.clearStorage();
      localStorage.removeItem('aiflorist_cid');

      close();

      if (onForgetComplete) {
        onForgetComplete();
      }

      render.systemError(t('deletedFresh'));
    } catch (err) {
      debug.error('[AI Florist] Forget me error:', err);
      render.systemError(t('unableDelete'));
    }
  }

  function buildRequesterHeaders(headersOrRequester, explicitRequester) {
    var headers = headersOrRequester;
    var requesterId = explicitRequester || '';

    if (typeof headersOrRequester === 'string') {
      requesterId = headersOrRequester;
      headers = {};
    }

    var next = Object.assign({}, headers || {});
    var fallbackRequester = requesterId || (identity && identity.rebuild ? identity.rebuild().id : '');
    if (fallbackRequester) {
      next['X-AIFLORIST-Requester-Id'] = fallbackRequester;
    }
    return next;
  }

  function detectLanguage() {
    var root = document.getElementById('aiflorist-chat-root');
    var raw =
      (root && root.dataset && (root.dataset.language || root.dataset.locale)) ||
      (document.documentElement && document.documentElement.lang) ||
      '';
    var base = String(raw).trim().toLowerCase().replace('_', '-').split('-')[0];
    if (base === 'zh' || base === 'ja') return base;
    return 'en';
  }

  function t(key) {
    var dict = DICT[uiLanguage] || DICT.en;
    return dict[key] || DICT.en[key] || key;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Public API ─────────────────────────────────────────────────────
  window.AIFlorist.history = {
    init: init,
    restoreActive: restoreActive,
    toggle: toggle,
    open: open,
    close: close,
    handleForgetMe: handleForgetMe,
    get isOpen() { return isOpen; },
  };
})();
